import * as Y from "yjs";

const REMOTE_ORIGIN = "note-remote-update";

export function uint8ArrayToBase64(bytes) {
    let binary = "";
    const chunkSize = 0x8000;
    for (let index = 0; index < bytes.length; index += chunkSize) {
        const chunk = bytes.subarray(index, index + chunkSize);
        binary += String.fromCharCode.apply(null, chunk);
    }
    return window.btoa(binary);
}

export function base64ToUint8Array(value) {
    if (!value) return new Uint8Array();
    const binary = window.atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
}

class NoteCollaborationProvider extends EventTarget {
    constructor({ url, noteNo }) {
        super();
        this.url = url;
        this.noteNo = noteNo;
        this.socket = null;
        this.connected = false;
        this.loaded = false;
        this.currentVersion = 0;
        this.snapshotVersion = 0;
        this.cacheVersion = 0;
        this.participants = [];
        this.document = new Y.Doc();
        this.remoteOrigin = REMOTE_ORIGIN;
        this.handleLocalUpdate = this.handleLocalUpdate.bind(this);
        this.document.on("update", this.handleLocalUpdate);
    }

    connect() {
        if (this.socket) return;
        this.socket = new WebSocket(this.url);
        this.socket.onopen = () => {
            this.connected = true;
            this.send("join", { note_no: this.noteNo });
            this.dispatchEvent(new CustomEvent("open"));
        };
        this.socket.onmessage = (event) => {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
            this.dispatchEvent(new CustomEvent(message.type, { detail: message.payload }));
        };
        this.socket.onclose = () => {
            this.connected = false;
            this.socket = null;
            this.dispatchEvent(new CustomEvent("close"));
        };
        this.socket.onerror = (event) => {
            this.dispatchEvent(new CustomEvent("error", { detail: event }));
        };
    }

    disconnect() {
        if (!this.socket) return;
        this.socket.close();
        this.socket = null;
    }

    destroy() {
        this.document.off("update", this.handleLocalUpdate);
        this.disconnect();
        this.document.destroy();
    }

    handleMessage(message) {
        const payload = message.payload || {};
        if (message.type === "snapshot") {
            this.applySnapshot(payload);
            return;
        }
        if (message.type === "update") {
            this.applyRemoteUpdate(payload);
            return;
        }
        if (message.type === "update_ack") {
            this.currentVersion = Math.max(this.currentVersion, payload.version || 0);
            this.dispatchEvent(new CustomEvent("version", { detail: this.getVersionState() }));
        }
        if (message.type === "materialize_ack") {
            this.cacheVersion = Math.max(this.cacheVersion, payload.cache_version || 0);
        }
        if (message.type === "awareness") {
            this.applyAwareness(payload);
        }
    }

    applySnapshot(payload) {
        const snapshot = base64ToUint8Array(payload.yjs_snapshot);
        const updates = payload.updates || [];
        this.document.transact(() => {
            if (snapshot.length > 0) {
                Y.applyUpdate(this.document, snapshot, this.remoteOrigin);
            }
            updates.forEach((item) => {
                const update = base64ToUint8Array(item.update);
                if (update.length > 0) {
                    Y.applyUpdate(this.document, update, this.remoteOrigin);
                }
            });
        }, this.remoteOrigin);
        this.currentVersion = payload.current_version || 0;
        this.snapshotVersion = payload.snapshot_version || 0;
        this.cacheVersion = payload.cache_version || 0;
        this.loaded = true;
        this.dispatchEvent(new CustomEvent("loaded", { detail: this.getVersionState() }));
    }

    applyRemoteUpdate(payload) {
        const update = base64ToUint8Array(payload.update);
        if (update.length > 0) {
            Y.applyUpdate(this.document, update, this.remoteOrigin);
        }
        this.currentVersion = Math.max(this.currentVersion, payload.version || 0);
        this.dispatchEvent(new CustomEvent("version", { detail: this.getVersionState() }));
    }

    handleLocalUpdate(update, origin) {
        if (origin === this.remoteOrigin || !this.loaded) return;
        this.sendUpdate({
            clientId: this.getClientId(),
            update: uint8ArrayToBase64(update),
        });
    }

    getClientId() {
        return String(this.document.clientID);
    }

    getVersionState() {
        return {
            currentVersion: this.currentVersion,
            snapshotVersion: this.snapshotVersion,
            cacheVersion: this.cacheVersion,
        };
    }

    getSnapshotBase64() {
        return uint8ArrayToBase64(Y.encodeStateAsUpdate(this.document));
    }

    isDocumentEmpty() {
        return this.document.getXmlFragment("default").length === 0;
    }

    send(type, payload = {}) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
        this.socket.send(JSON.stringify({ type, payload }));
        return true;
    }

    sendUpdate({ clientId, update }) {
        return this.send("update", {
            client_id: clientId,
            update,
        });
    }

    sendMaterialize(payload) {
        return this.send("materialize", payload);
    }

    sendAwareness(payload) {
        return this.send("awareness", payload);
    }

    applyAwareness(payload) {
        if ((payload?.event || "") !== "snapshot") return;
        this.participants = Array.isArray(payload.participants) ? payload.participants : [];
        this.dispatchEvent(
            new CustomEvent("participants", {
                detail: {
                    participants: this.participants,
                },
            }),
        );
    }
}

export default NoteCollaborationProvider;
